import React, { useState, useEffect } from 'react';
import { SearchIcon, CloseIcon } from '@/components/icons';
import { Button } from '@/components/ui';
import { useFilterVideos, useHomePageHighlights, useUpdateHomePageHighlights, useSearchVideos } from '@/lib/api/hooks/video';
import { showToast } from '@/lib/toast';
import { Video } from '@/lib/api/types';
import { WebsitePageSection } from '@/types';
import { X } from 'lucide-react';

// Helper function to format date
const formatPublishedDate = (dateString: string | null): string => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return '';
    }
};

interface InlineMultimediaVideoEditorProps {
    data: WebsitePageSection;
    onSave: (data: Partial<WebsitePageSection>) => void;
    onCancel: () => void;
}

const InlineMultimediaVideoEditor: React.FC<InlineMultimediaVideoEditorProps> = ({
    onCancel,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [selectedVideos, setSelectedVideos] = useState<Video[]>([]);


    const { data: allVideosData, isLoading: isAllLoading } = useFilterVideos(
        { page: 1, size: 20 },
        {}
    );

    const { data: searchResultsData, isLoading: isSearchLoading } = useSearchVideos(
        { page: 1, size: 20 },
        searchQuery,
        !!searchQuery
    );

    const allVideos = searchQuery
        ? (searchResultsData?.data?.data || [])
        : (allVideosData?.data?.data || []);
    const videos = allVideos.filter((video: Video) => !selectedIds.includes(video.videoUploadId));

    const isLoading = searchQuery ? isSearchLoading : isAllLoading;

    const { data: highlightsData } = useHomePageHighlights('HOME_PAGE_IS_MULTIMEDIA');

    const updateHighlightsMutation = useUpdateHomePageHighlights();

    useEffect(() => {
        if (highlightsData?.data) {
            const currentIds = highlightsData.data.map((video: Video) => video.videoUploadId);
            setSelectedIds(currentIds);
            setSelectedVideos(highlightsData.data);
        }
    }, [highlightsData]);

    const handleToggleSelect = (videoId: string) => {
        const isAlreadySelected = selectedIds.includes(videoId);

        if (isAlreadySelected) {
            setSelectedIds(prev => prev.filter(id => id !== videoId));
            setSelectedVideos(prevVideos => prevVideos.filter(v => v.videoUploadId !== videoId));
        } else {
            if (selectedIds.length >= 4) {
                showToast.error("You can only select up to 4 videos.");
                return;
            }
            const videoToAdd = allVideos.find((v: Video) => v.videoUploadId === videoId);
            if (videoToAdd) {
                setSelectedIds(prev => [...prev, videoId]);
                setSelectedVideos(prevVideos => [...prevVideos, videoToAdd]);
            }
        }
    };

    const handleSave = async () => {
        try {
            await updateHighlightsMutation.mutateAsync({ videoUploadIds: selectedIds, type: 'HOME_PAGE_IS_MULTIMEDIA' });
            showToast.success('Multimedia highlights updated successfully');
            onCancel(); // exit edit mode
        } catch (error) {
            console.error('Failed to update highlights:', error);
            showToast.error('Failed to update highlights');
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">

            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="font-semibold text-lg text-gray-900">Replace Highlight</h3>
                <button
                    onClick={onCancel}
                    className="text-gray-500 hover:text-gray-700 p-1"
                >
                    <CloseIcon className="w-5 h-5" />
                </button>
            </div>


            {selectedVideos.length > 0 && (
                <div className="p-4 border-b border-gray-200 bg-wine-50/30">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-700">Currently Selected ({selectedVideos.length}/4)</h4>
                    </div>
                    <div className="space-y-2">
                        {selectedVideos.map((video) => (
                            <div
                                key={`selected-${video.videoUploadId}`}
                                className="flex items-start gap-3 bg-white border border-[#681628] rounded-lg p-3 group"
                            >
                                <div className="w-16 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                                    <img
                                        src={"https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-11_sxxnhn.png"}
                                        alt={video.title}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h5 className="text-sm font-medium text-gray-900 truncate">{video.title}</h5>
                                    {video.datePublished ? (
                                        <p className="text-xs text-gray-500 mt-0.5"><span className="font-medium">Published date:</span> {formatPublishedDate(video.datePublished)}</p>
                                    ) : (
                                        <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{video.description || 'No description'}</p>
                                    )}
                                </div>
                                <button
                                    onClick={() => handleToggleSelect(video.videoUploadId)}
                                    className="p-1 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                                    title="Remove from selection"
                                >
                                    <X className="w-4 h-4 text-gray-500 group-hover:text-[#681628]" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}


            <div className="p-4 border-b border-gray-200">
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search published content"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full h-10 pl-4 pr-12 bg-gray-50 text-base font-normal text-gray-900 placeholder-gray-600 rounded border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#951F39] focus:border-transparent"
                    />
                    {searchQuery ? (
                        <button
                            type="button"
                            onClick={() => setSearchQuery('')}
                            className="absolute right-0 top-0 w-10 h-10 bg-[#951F39] rounded-r flex items-center justify-center hover:bg-[#801b31] transition-colors"
                        >
                            <CloseIcon className="w-5 h-5 text-white" />
                        </button>
                    ) : (
                        <div className="absolute right-0 top-0 w-10 h-10 bg-[#951F39] rounded-r flex items-center justify-center">
                            <SearchIcon className="w-5 h-5 text-white" />
                        </div>
                    )}
                </div>
            </div>

            <div className="max-h-[400px] overflow-y-auto p-4 space-y-3">
                {isLoading ? (
                    <div className="text-center py-8 text-gray-500">Loading videos...</div>
                ) : videos.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No videos found</div>
                ) : (
                    videos.map((video: Video) => (
                        <div
                            key={video.videoUploadId}
                            onClick={() => handleToggleSelect(video.videoUploadId)}
                            className="flex items-start gap-4 p-3 rounded-lg border cursor-pointer transition-all border-gray-200 hover:border-gray-300"
                        >

                            <div className="relative w-24 h-16 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                                <img
                                    src={"https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-11_sxxnhn.png"}
                                    alt={video.title}
                                    className="w-full h-full object-cover"
                                />
                            </div>

                            <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-gray-900 text-sm truncate">{video.title}</h4>
                                {video.datePublished ? (
                                    <p className="text-xs text-gray-500 mt-0.5"><span className="font-medium">Published date:</span> {formatPublishedDate(video.datePublished)}</p>
                                ) : (
                                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{video.description}</p>
                                )}
                            </div>

                            <div className="flex-shrink-0 pt-1">
                                <div className="w-6 h-6 rounded-full border border-gray-300" />
                            </div>
                        </div>
                    ))
                )}
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
                <Button variant="secondary" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={updateHighlightsMutation.isPending}
                >
                    {updateHighlightsMutation.isPending ? 'Saving...' : 'Replace Highlight'}
                </Button>
            </div>
        </div>
    );
};

export default InlineMultimediaVideoEditor;
